import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuditData } from '@/hooks/useAuditData';
import { getScoreColor } from '@/lib/utils';
import { Loader2, ArrowRight, CheckCircle2, AlertCircle, XCircle, Download, Printer, Filter } from 'lucide-react';

export function Dashboard() {
  const { data, loading, error } = useAuditData();
  const [priorityFilter, setPriorityFilter] = useState<number | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">
          No audit data found. Run <code className="text-xs bg-secondary px-2 py-1 rounded">npm run audit</code> first.
        </p>
      </div>
    );
  }

  const { summary, sites } = data;

  return (
    <div className="max-w-6xl mx-auto space-y-16 py-8">
      {/* Header */}
      <header className="space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider">Digital Audit Report</p>
            <h1 className="text-4xl font-light">Executive Summary</h1>
            <p className="text-muted-foreground">
              {data.meta.totalSites} restaurants analyzed on{' '}
              {new Date(data.meta.generatedAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
          <div className="flex gap-2 no-print">
            <button
              onClick={() => {
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-results-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
            >
              <Download className="h-4 w-4" />
              Export JSON
            </button>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print / PDF
            </button>
          </div>
        </div>
      </header>

      {/* Key Metrics */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-8">
        <MetricBlock
          label="Avg Performance"
          value={summary.averageScores.performance}
          isScore
        />
        <MetricBlock
          label="Avg SEO Score"
          value={summary.averageScores.seo}
          isScore
        />
        <MetricBlock
          label="Tracking Coverage"
          value={`${summary.trackingCoverage.ga4}/${data.meta.totalSites}`}
          subtext="sites with GA4"
        />
        <MetricBlock
          label="Action Items"
          value={summary.totalRecommendations}
          subtext="total recommendations"
        />
      </section>

      {/* Insights Section */}
      {data.insights && (
        <section className="space-y-6">
          <h2 className="text-lg font-medium text-muted-foreground uppercase tracking-wider">Analysis</h2>

          <div className="grid gap-6">
            {data.insights.summary && (
              <InsightCard
                title="Executive Summary"
                content={data.insights.summary}
              />
            )}

            <div className="grid md:grid-cols-3 gap-6">
              {data.insights.vitals && (
                <InsightCard
                  title="Performance"
                  content={data.insights.vitals}
                  compact
                />
              )}
              {data.insights.seo && (
                <InsightCard
                  title="SEO"
                  content={data.insights.seo}
                  compact
                />
              )}
              {data.insights.tracking && (
                <InsightCard
                  title="Tracking"
                  content={data.insights.tracking}
                  compact
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Sites List */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-muted-foreground uppercase tracking-wider">All Sites</h2>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors no-print ${
              showFilters ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground hover:text-foreground'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filter by Priority
          </button>
        </div>

        {/* Priority Filters */}
        {showFilters && (
          <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-lg no-print">
            <span className="text-sm text-muted-foreground">Show sites with:</span>
            <div className="flex gap-2">
              <PriorityFilterButton
                active={priorityFilter === 'all'}
                onClick={() => setPriorityFilter('all')}
              >
                All Sites
              </PriorityFilterButton>
              <PriorityFilterButton
                active={priorityFilter === 1}
                onClick={() => setPriorityFilter(1)}
                color="red"
              >
                Critical Issues
              </PriorityFilterButton>
              <PriorityFilterButton
                active={priorityFilter === 2}
                onClick={() => setPriorityFilter(2)}
                color="orange"
              >
                High Priority
              </PriorityFilterButton>
              <PriorityFilterButton
                active={priorityFilter === 3}
                onClick={() => setPriorityFilter(3)}
                color="yellow"
              >
                Medium Priority
              </PriorityFilterButton>
            </div>
          </div>
        )}

        <div className="divide-y divide-border">
          {sites
            .filter((site) => {
              if (priorityFilter === 'all') return true;
              // Filter sites that have recommendations with the selected priority
              return site.recommendations.some((r: any) => r.priority === priorityFilter);
            })
            .map((site) => (
              <SiteRow key={site.id} site={site} priorityFilter={priorityFilter} />
            ))}
        </div>

        {priorityFilter !== 'all' && sites.filter((site) =>
          site.recommendations.some((r: any) => r.priority === priorityFilter)
        ).length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            No sites with {priorityFilter === 1 ? 'critical' : priorityFilter === 2 ? 'high priority' : 'medium priority'} issues found.
          </p>
        )}
      </section>
    </div>
  );
}

function MetricBlock({
  label,
  value,
  isScore,
  subtext,
}: {
  label: string;
  value: number | string;
  isScore?: boolean;
  subtext?: string;
}) {
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-1">{label}</p>
      <p className={`text-4xl font-light ${isScore ? getScoreColor(value as number) : ''}`}>
        {value}
      </p>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </div>
  );
}

function InsightCard({
  title,
  content,
  compact,
}: {
  title: string;
  content: string;
  compact?: boolean;
}) {
  return (
    <div className={`bg-secondary/30 rounded-lg ${compact ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-medium mb-2 ${compact ? 'text-sm' : 'text-base'}`}>{title}</h3>
      <p className={`text-muted-foreground leading-relaxed ${compact ? 'text-sm' : 'text-base'}`}>
        {content}
      </p>
    </div>
  );
}

function PriorityFilterButton({
  children,
  active,
  onClick,
  color,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  color?: 'red' | 'orange' | 'yellow';
}) {
  const activeColors = {
    red: 'bg-red-500/20 text-red-400 border-red-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  };

  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs rounded border transition-colors ${
        active
          ? color
            ? activeColors[color]
            : 'bg-foreground text-background border-foreground'
          : 'bg-secondary text-muted-foreground border-border hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

function SiteRow({ site, priorityFilter }: { site: any; priorityFilter?: number | 'all' }) {
  const overallScore = Math.round(
    (site.vitals.performanceScore + site.seo.score) / 2
  );

  const getStatusIcon = () => {
    if (overallScore >= 70) return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    if (overallScore >= 50) return <AlertCircle className="h-4 w-4 text-orange-500" />;
    return <XCircle className="h-4 w-4 text-red-500" />;
  };

  // Calculate tracking coverage score
  const trackingCoverage = site.tracking.events?.trackingCoverage?.overallScore;
  const hasTrackingScore = typeof trackingCoverage === 'number';
  const hasTrackingGaps = !site.tracking.ga4.present || !site.tracking.facebookPixel.present;

  // Count recommendations by priority
  const criticalCount = site.recommendations.filter((r: any) => r.priority === 1).length;
  const highCount = site.recommendations.filter((r: any) => r.priority === 2).length;
  const mediumCount = site.recommendations.filter((r: any) => r.priority === 3).length;

  // Get count for current filter
  const getFilteredCount = () => {
    if (priorityFilter === 1) return criticalCount;
    if (priorityFilter === 2) return highCount;
    if (priorityFilter === 3) return mediumCount;
    return null;
  };

  const getTrackingDisplay = () => {
    if (hasTrackingScore) {
      return {
        value: `${trackingCoverage}%`,
        color: getScoreColor(trackingCoverage),
      };
    }
    return {
      value: hasTrackingGaps ? 'Incomplete' : 'Complete',
      color: hasTrackingGaps ? 'text-orange-500' : 'text-green-500',
    };
  };

  const trackingDisplay = getTrackingDisplay();
  const filteredCount = getFilteredCount();

  return (
    <Link
      to={`/site/${site.id}`}
      className="flex items-center justify-between py-5 hover:bg-secondary/20 -mx-4 px-4 rounded transition-colors group"
    >
      <div className="flex items-center gap-4">
        {getStatusIcon()}
        <div>
          <h3 className="font-medium group-hover:text-white transition-colors">{site.name}</h3>
          <p className="text-sm text-muted-foreground">{new URL(site.url).hostname}</p>
        </div>
      </div>

      <div className="flex items-center gap-8">
        <div className="text-right hidden sm:block">
          <p className="text-sm text-muted-foreground">Performance</p>
          <p className={`font-medium ${getScoreColor(site.vitals.performanceScore)}`}>
            {site.vitals.performanceScore}
          </p>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-sm text-muted-foreground">SEO</p>
          <p className={`font-medium ${getScoreColor(site.seo.score)}`}>
            {site.seo.score}
          </p>
        </div>
        <div className="text-right hidden md:block w-24">
          <p className="text-sm text-muted-foreground">Tracking</p>
          <p className={`text-sm font-medium ${trackingDisplay.color}`}>
            {trackingDisplay.value}
          </p>
        </div>
        {/* Show issue count when filtering */}
        {filteredCount !== null && (
          <div className="text-right w-16">
            <p className="text-sm text-muted-foreground">Issues</p>
            <p className={`text-sm font-medium ${
              priorityFilter === 1 ? 'text-red-400' :
              priorityFilter === 2 ? 'text-orange-400' :
              'text-yellow-400'
            }`}>
              {filteredCount}
            </p>
          </div>
        )}
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-white transition-colors" />
      </div>
    </Link>
  );
}
